<script setup lang="ts" generic="T extends SelectableItem = SelectableItem">
  import { computed, defineAsyncComponent, h, ref, watch, type PropType, type VNode } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { ANIMATION_PRESETS } from '@common/animations-layer/constants/animations'

  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '@common/core-layer/utils'

  import type { ActionButton, ItemRendererParams, SelectableItem } from '../types/selectable-list'

  const draggable = defineAsyncComponent(() => import('vuedraggable-es'))

  const props = defineProps({
    items: {
      type: Array as PropType<T[]>,
      default: () => [],
    },
    activeValue: {
      type: [String, Number],
      default: undefined,
    },
    // Render function for items (like HLDataTable's cellFormatter)
    itemRenderer: {
      type: Function as PropType<(params: ItemRendererParams<T>) => VNode>,
      required: false,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    // Active state customization
    activeBackgroundColor: {
      type: String,
      default: 'bg-primary-50',
    },
    activeBorderColor: {
      type: String,
      default: 'border-primary-500',
    },
    activeTextColor: {
      type: String,
      default: 'text-primary-500',
    },
    inactiveTextColor: {
      type: String,
      default: 'text-700',
    },
    hoverBackgroundColor: {
      type: String,
      default: 'hover:bg-hover',
    },
    // Empty state customization
    emptyStateIcon: {
      type: String,
      default: '📋',
    },
    emptyStateTitle: {
      type: String,
      default: undefined,
    },
    emptyStateDescription: {
      type: String,
      default: undefined,
    },
    // Actions for empty state
    actions: {
      type: Array as PropType<ActionButton[]>,
      default: () => [],
    },
    // Loading text
    loadingText: {
      type: String,
      default: undefined,
    },
    // Optional extra class for each item wrapper (e.g. rounded-md for tab style)
    itemWrapperClass: {
      type: String,
      default: '',
    },
    // When true, list items can be reordered via drag-and-drop; emits itemReorder with new order
    allowReorder: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['itemClick', 'itemAction', 'refresh', 'itemReorder'])

  const { t } = useI18n()

  const effectiveLoadingText = computed(() => props.loadingText ?? t('selectableList.loading'))
  const effectiveEmptyStateTitle = computed(() => props.emptyStateTitle ?? t('selectableList.empty.title'))
  const effectiveEmptyStateDescription = computed(() => props.emptyStateDescription ?? t('selectableList.empty.description'))

  const visibleActions = computed(() => props.actions.filter(action => action.visible !== false))

  const getItemId = (item: T) => item.id || item._id || item.name

  const isItemActive = (item: T) => {
    if (props.activeValue === undefined) return false
    const itemId = item.id || item._id || item.name
    return itemId === props.activeValue
  }

  const isItemClickable = (item: T) => {
    return !item.disabled && !item.isSkeleton
  }

  // Event handlers
  const handleItemClick = (item: T) => {
    emit('itemClick', item)
  }

  const handleActionClick = (action: ActionButton) => {
    emit('itemAction', { actionKey: action.key, action })
  }

  const handleRefresh = () => {
    emit('refresh')
  }

  // Default item renderer
  const defaultItemRenderer = (params: ItemRendererParams<T>) => {
    return h(
      'div',
      {
        class: 'px-4 py-2',
      },
      params.item.name
    )
  }

  const renderItem = props.itemRenderer || defaultItemRenderer

  // Local mutable list for vuedraggable when allowReorder is true
  const localList = ref<SelectableItem[]>([])

  const isReordering = ref(false)

  const onDragStart = () => {
    isReordering.value = true
  }

  const onDragEnd = () => {
    isReordering.value = false
  }

  const handleItemReorder = () => {
    emit('itemReorder', [...localList.value])
  }

  // Prevent moving disabled/divider items (they stay fixed in place); block drag of disabled, block drop on disabled
  const handleMove = (evt: { draggedContext?: { element?: SelectableItem }; relatedContext: { element?: SelectableItem } }) => {
    const dragged = evt.draggedContext?.element
    const related = evt.relatedContext?.element
    if (dragged?.disabled) return false
    return !related?.disabled
  }

  const dragOptions = computed(() => ({
    animation: 200,
    disabled: !props.allowReorder,
    fallbackOnBody: true,
    filter: '.selectable-list-item--no-drag', // Prevent drag start on disabled/divider items
    swapThreshold: 0.65,
  }))

  // Use TransitionGroup as draggable root; disable move transition during drag so reorder snaps
  const transitionGroupData = computed(() => ({
    ...ANIMATION_PRESETS.LIST_STAGGER_FAST,
    name: isReordering.value ? 'list-stagger-fast-nomove' : ANIMATION_PRESETS.LIST_STAGGER_FAST.name,
    tag: 'div',
    class: 'items-container w-full h-full',
  }))

  watch(
    () => props.items,
    val => {
      // Do not override localList while a drag is in progress to avoid visual glitches
      if (!isReordering.value) {
        localList.value = val ? [...val] : []
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div class="selectable-list w-full h-full">
    <!-- Loading State -->
    <div v-if="loading">
      <slot v-if="$slots.loading" name="loading" />
      <div v-else class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <CPText class="text-secondary">{{ effectiveLoadingText }}</CPText>
      </div>
    </div>

    <!-- Items List: single draggable with TransitionGroup so everyone gets list animations -->
    <div v-else-if="(items?.length ?? 0) > 0" class="w-full">
      <draggable
        v-model="localList"
        v-bind="dragOptions"
        :item-key="getItemId"
        :move="handleMove"
        tag="transition-group"
        :component-data="transitionGroupData"
        @choose="onDragStart"
        @update="handleItemReorder"
        @end="onDragEnd"
      >
        <template #item="{ element: item, index }">
          <div
            :key="getItemId(item)"
            :style="{ '--index': index }"
            :class="[
              'selectable-list-item',
              {
                'selectable-list-item--active': isItemActive(item),
                'selectable-list-item--no-drag': item.disabled,
                'cursor-pointer': isItemClickable(item),
                'cursor-default': !isItemClickable(item),
              },
              isItemClickable(item) && !isItemActive(item) ? props.hoverBackgroundColor : '',
              isItemActive(item) ? `${props.activeBackgroundColor} !${props.activeTextColor}` : props.inactiveTextColor,
              props.itemWrapperClass,
            ]"
            :role="isItemClickable(item) ? 'button' : undefined"
            :tabindex="isItemClickable(item) ? 0 : undefined"
            @click="isItemClickable(item) ? handleItemClick(item) : null"
            @keydown.enter="isItemClickable(item) ? handleItemClick(item) : null"
            @keydown.space.prevent="isItemClickable(item) ? handleItemClick(item) : null"
          >
            <component
              :is="
                renderItem({
                  item,
                  index,
                  isActive: isItemActive(item),
                  onClick: () => handleItemClick(item),
                })
              "
            />
          </div>
        </template>
      </draggable>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <div class="text-6xl mb-4">{{ emptyStateIcon }}</div>
      <h2 class="text-xl text-secondary mb-2">{{ effectiveEmptyStateTitle }}</h2>
      <p v-if="effectiveEmptyStateDescription" class="text-muted mb-6">{{ effectiveEmptyStateDescription }}</p>

      <!-- Action Buttons -->
      <div v-if="visibleActions.length > 0" class="flex flex-wrap gap-2 justify-center">
        <CPButton
          v-for="action in visibleActions"
          :id="createElementId('selectable-list', 'action', action.key)"
          :key="action.key"
          :type="action.type || 'primary'"
          :size="action.size || 'md'"
          @click="handleActionClick(action)"
        >
          {{ action.label }}
        </CPButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .selectable-list {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .items-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
    flex: 1;
    min-height: 0; /* Important for flex children to shrink */
  }

  /* Disable move transition during drag so reorder snaps without animating */
  .list-stagger-fast-nomove-move {
    transition: none;
  }
</style>
